import { useCustom } from '@refinedev/core';
import { Spin } from 'antd';
import { CreditCard } from 'lucide-react';
import type { FC } from 'react';

import BillingStatsCards from '@/components/ops/BillingStatsCards';
import GracePeriodPanel from '@/components/ops/GracePeriodPanel';
import PlanDistributionChart from '@/components/ops/PlanDistributionChart';
import SubscriptionsTable from '@/components/ops/SubscriptionsTable';

export interface BillingStats {
  mrr: number;
  arr: number;
  totalSubscriptions: number;
  activeSubscriptions: number;
  pastDueSubscriptions: number;
  canceledThisMonth: number;
  churnRate: number;
  byPlan: Record<string, number>;
  mrrGrowth: number;
  newSubscriptionsThisMonth: number;
}

const SubscriptionsDashboardPage: FC = () => {
  const { data, isLoading, isError, refetch } = useCustom<{ data: BillingStats }>({
    url: '/api/v1/admin/billing/stats',
    method: 'get',
  });

  const stats = data?.data?.data;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <GradientHeader />
        <div className="fc-card border-red-300 p-6 dark:border-red-800">
          <p className="font-medium text-red-600 dark:text-red-400">
            Impossible de charger les statistiques de facturation.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <GradientHeader />
      <BillingStatsCards stats={stats} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <PlanDistributionChart byPlan={stats?.byPlan} />
        <NewSubscriptionsSummary stats={stats} />
      </div>

      <GracePeriodPanel />
      <SubscriptionsTable onMutationSuccess={() => void refetch()} />
    </div>
  );
};

const GradientHeader: FC = () => (
  <div className="fc-gradient-header rounded-2xl p-6 text-white">
    <div className="flex items-center gap-3">
      <CreditCard className="h-6 w-6" />
      <div>
        <h1 className="text-xl font-bold">Abonnements</h1>
        <p className="mt-1 text-sm text-blue-100">Gestion Stripe & Métriques MRR</p>
      </div>
    </div>
  </div>
);

const SummaryRow: FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm text-slate-600 dark:text-slate-300">{label}</span>
    <span className={`text-lg font-bold ${color}`}>{value}</span>
  </div>
);

const NewSubscriptionsSummary: FC<{ stats: BillingStats | undefined }> = ({ stats }) => (
  <div className="fc-card p-5">
    <h3 className="mb-4 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
      Activité ce mois
    </h3>
    <div className="space-y-3">
      <SummaryRow label="Nouveaux abonnés" value={stats?.newSubscriptionsThisMonth ?? 0} color="text-emerald-600 dark:text-emerald-400" />
      <SummaryRow label="Annulés" value={stats?.canceledThisMonth ?? 0} color="text-red-600 dark:text-red-400" />
      <SummaryRow label="Past Due" value={stats?.pastDueSubscriptions ?? 0} color="text-amber-600 dark:text-amber-400" />
    </div>
  </div>
);

export default SubscriptionsDashboardPage;
